/**
 * BulkEnrollUpload.js — CSV/XLSX upload for bulk org enrollment
 *
 * Placed inside the Organizations tab. Accepts a file,
 * shows preview of parsed rows, then submits to the backend.
 *
 * EXPECTED COLUMNS (flexible header matching):
 *   org_name, org_domain, org_type, sector, email, firstname, lastname,
 *   startdate, enddate, type/plan, ckgpoc, role
 */

import React, { useState, useRef } from 'react';
import { Upload, FileText, Check, AlertCircle, X } from 'lucide-react';
import { apiService } from '../../services/api';

// =========================================
// Component
// =========================================

const BulkEnrollUpload = ({ onComplete }) => {
  const [file, setFile] = useState(null);
  const [autoApprove, setAutoApprove] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    const ext = selected.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      setError('Please upload a CSV or XLSX file.');
      return;
    }

    setFile(selected);
    setError(null);
    setResults(null);
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsUploading(true);
    setError(null);
    setResults(null);

    try {
      const result = await apiService.admin.bulkEnroll(file, autoApprove);
      setResults(result);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onComplete) onComplete();
    } catch (err) {
      const msg = err.response?.data?.message || 'Upload failed. Please check the file format.';
      const details = err.response?.data?.errors;
      setError(msg);
      if (details) {
        setResults({ validationErrors: details });
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setResults(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="bulk-enroll">
      <div className="bulk-enroll-header">
        <h3>Bulk Enrollment</h3>
        <p className="bulk-enroll-desc">
          Upload a CSV or XLSX file to register organizations and their users.
        </p>
      </div>

      {/* File input */}
      <div className="bulk-enroll-controls">
        <div className="bulk-enroll-file">
          <label className="file-upload-button" htmlFor="bulk-enroll-input">
            <Upload size={16} />
            <span>{file ? file.name : 'Choose file…'}</span>
          </label>
          <input
            id="bulk-enroll-input"
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {file && (
            <button className="bulk-enroll-clear" onClick={handleClear} title="Clear">
              <X size={14} />
            </button>
          )}
        </div>

        <label className="bulk-enroll-checkbox">
          <input
            type="checkbox"
            checked={autoApprove}
            onChange={(e) => setAutoApprove(e.target.checked)}
          />
          <span>Auto-approve (create accounts immediately)</span>
        </label>

        <button
          className="add-button"
          onClick={handleUpload}
          disabled={!file || isUploading}
        >
          {isUploading ? 'Uploading…' : 'Upload & Enroll'}
        </button>
      </div>

      {/* Expected format hint */}
      <div className="bulk-enroll-format">
        <FileText size={14} />
        <span>
          Required columns: <strong>org_name</strong>, <strong>org_domain</strong>,{' '}
          <strong>email</strong>, <strong>firstname</strong>, <strong>lastname</strong>.
          Optional: org_type, sector, startdate, enddate, plan, ckgpoc, role.
        </span>
      </div>

      {/* Error display */}
      {error && (
        <div className="error-message">
          <AlertCircle size={16} style={{ marginRight: 8, flexShrink: 0 }} />
          {error}
        </div>
      )}

      {/* Validation errors (per-row) */}
      {results?.validationErrors && (
        <div className="bulk-enroll-errors">
          <h4>Validation Errors</h4>
          {results.validationErrors.map((e, i) => (
            <div key={i} className="bulk-error-row">
              Row {e.row}: missing {e.missing.join(', ')} {e.email !== '(blank)' && `(${e.email})`}
            </div>
          ))}
        </div>
      )}

      {/* Success results */}
      {results?.summary && (
        <div className="bulk-enroll-results">
          <h4>
            <Check size={16} style={{ marginRight: 6, color: '#1D9E75' }} />
            Enrollment Complete
          </h4>

          <div className="bulk-results-grid">
            <div className="bulk-result-item">
              <span className="bulk-result-label">Rows processed</span>
              <span className="bulk-result-value">{results.summary.totalRows}</span>
            </div>
            <div className="bulk-result-item">
              <span className="bulk-result-label">Orgs created</span>
              <span className="bulk-result-value">{results.summary.orgsCreated}</span>
            </div>
            <div className="bulk-result-item">
              <span className="bulk-result-label">Orgs existing</span>
              <span className="bulk-result-value">{results.summary.orgsExisting}</span>
            </div>
            <div className="bulk-result-item">
              <span className="bulk-result-label">Users created</span>
              <span className="bulk-result-value">{results.summary.usersCreated}</span>
            </div>
            <div className="bulk-result-item">
              <span className="bulk-result-label">Users skipped</span>
              <span className="bulk-result-value">{results.summary.usersSkipped}</span>
            </div>
            {results.summary.usersApproved > 0 && (
              <div className="bulk-result-item">
                <span className="bulk-result-label">Users approved</span>
                <span className="bulk-result-value">{results.summary.usersApproved}</span>
              </div>
            )}
            {results.summary.errors > 0 && (
              <div className="bulk-result-item bulk-result-error">
                <span className="bulk-result-label">Errors</span>
                <span className="bulk-result-value">{results.summary.errors}</span>
              </div>
            )}
          </div>

          {/* Show temporary passwords if auto-approved */}
          {results.details?.usersApproved?.length > 0 && (
            <div className="bulk-passwords">
              <h4>Temporary Passwords</h4>
              <p className="bulk-passwords-note">
                These are shown once. Copy them now or rely on the welcome email magic links.
              </p>
              <table className="bulk-passwords-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Password</th>
                    <th>Email Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {results.details.usersApproved.map((u, i) => (
                    <tr key={i}>
                      <td>{u.email}</td>
                      <td><code>{u.temporaryPassword}</code></td>
                      <td>{u.welcomeEmailSent ? '✓' : '✗'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Skipped details */}
          {results.details?.usersSkipped?.length > 0 && (
            <details className="bulk-details">
              <summary>{results.details.usersSkipped.length} user(s) skipped</summary>
              {results.details.usersSkipped.map((u, i) => (
                <div key={i} className="bulk-detail-row">
                  {u.email}: {u.reason}
                </div>
              ))}
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkEnrollUpload;
